/* eslint-disable no-console */
import type { JwtPayload } from 'jwt-decode';
import type { PropsWithChildren } from 'react';
import * as Sentry from '@sentry/react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { AuthContextProps } from './AuthContext';

import { AuthContext } from './AuthContext';
import type { AuthSessionData } from './AuthSession';

import { getSession } from './AuthSession';
import {
	completeTwoFactorSignIn,
	isAuthenticated,
	refreshAccessToken,
	signInWithEmail,
	signInWithGoogle,
	signOut,
} from './AuthUtils';
import { AuthenticationError, TokenRefreshError } from './Errors';

export type UserAuthJwtPayload = {
	userGroupId: number;
	preferredCurrency: string;
	preferredLang: string;
} & JwtPayload;

/**
 * AuthProvider component that manages authentication state and operations
 *
 * Provides authentication context to the entire application including:
 * - Sign in/out functionality
 * - Token refresh handling
 * - Authentication state management
 * - Error handling and loading states
 */
function identifySentryUser({
	userId,
	email,
	userGroupId,
	loginMethod,
	preferredCurrency,
	preferredLang,
}: AuthSessionData): void {
	Sentry.setUser({
		id: userId,
		email,
	});

	Sentry.setContext('userSession', {
		userGroupId,
		loginMethod,
		preferredCurrency,
		preferredLang,
	});
}

const AuthProvider = ({ children }: PropsWithChildren) => {
	const [error, setError] = useState<Error | undefined>(undefined);
	const [isLoading, setIsLoading] = useState<boolean>(false);

	// Development helper - must be at top level to follow React hooks rules
	useEffect(() => {
		if (import.meta.env.DEV) {
			console.group('🔐 Auth State Change');
			console.log('Is Loading:', isLoading);
			console.log('Has Error:', !!error);
			console.log('Is Authenticated:', isAuthenticated());
			console.groupEnd();
		}
	}, [isLoading, error]);

	const handleSignIn = useCallback(
		async (email: string, password: string, invitationCode?: string): Promise<{ twoFactorToken?: string }> => {
			// Prevent multiple simultaneous sign-in attempts
			if (isLoading) {
				return {};
			}

			try {
				setIsLoading(true);
				setError(undefined);

				const result = await signInWithEmail(email, password, invitationCode);
				if (result.status === 'two-factor-required') {
					return { twoFactorToken: result.twoFactorToken };
				}

				identifySentryUser({ ...result.session, email });
				return {};
			} catch (err) {
				// AuthUtils already handles signOut for authentication errors
				// InvitationErrors don't trigger signOut to preserve session
				setError(err as Error);

				throw err; // Re-throw to allow caller to handle specific errors if needed
			} finally {
				setIsLoading(false);
			}
		},
		[isLoading],
	);

	const handleCompleteTwoFactorSignIn = useCallback(
		async (twoFactorToken: string, code: string, email: string): Promise<void> => {
			try {
				setIsLoading(true);
				setError(undefined);

				identifySentryUser(await completeTwoFactorSignIn(twoFactorToken, code, email));
			} catch (err) {
				setError(err as Error);
				throw err;
			} finally {
				setIsLoading(false);
			}
		},
		[],
	);

	const handleRefreshToken = useCallback(async (force = false): Promise<void> => {
		try {
			setIsLoading(true);
			setError(undefined);

			// Validate current session before attempting refresh
			const currentSession = getSession();
			if (!currentSession && !force) {
				throw new TokenRefreshError('No session found for refresh', 'INVALID_TOKEN', true);
			}

			await refreshAccessToken(force);
			// refreshAccessToken now throws TokenRefreshError instead of returning null
		} catch (err) {
			let shouldSignOut = false;

			if (err instanceof TokenRefreshError) {
				shouldSignOut = err.shouldSignOut;
			} else if (err instanceof AuthenticationError) {
				shouldSignOut = true;
			} else {
				// Unknown error - be conservative and don't sign out unless clearly auth-related
				shouldSignOut = false;
			}

			if (shouldSignOut) {
				signOut();
			}

			setError(err as Error);
		} finally {
			setIsLoading(false);
		}
	}, []);

	const handleSignOut = useCallback((error: Error | undefined = undefined) => {
		signOut();
		setError(error);
		setIsLoading(false);
		Sentry.setUser(null);
		Sentry.setContext('userSession', null);
	}, []);

	const handleGoogleSignIn = useCallback(
		async (idToken: string, invitationCode?: string): Promise<{ isNewUser: boolean }> => {
			// Prevent multiple simultaneous sign-in attempts
			if (isLoading) {
				return { isNewUser: false };
			}

			try {
				setIsLoading(true);
				setError(undefined);

				// signInWithGoogle now throws AuthenticationError instead of returning null
				const { preferredCurrency, preferredLang, userGroupId, userId, loginMethod, email, isNewUser } =
					await signInWithGoogle(idToken, invitationCode);

				Sentry.setUser({
					id: userId,
					email,
				});

				Sentry.setContext('userSession', {
					userGroupId,
					loginMethod,
					preferredCurrency,
					preferredLang,
				});

				return { isNewUser };
			} catch (err) {
				// AuthUtils already handles signOut for authentication errors
				// InvitationErrors don't trigger signOut to preserve session
				setError(err as Error);

				throw err; // Re-throw to allow caller to handle specific errors if needed
			} finally {
				setIsLoading(false);
			}
		},
		[isLoading],
	);

	const handleIsAuthenticated = useCallback((): boolean => isAuthenticated(), []);

	const contextValue = useMemo<AuthContextProps>(() => {
		return {
			signIn: handleSignIn,
			completeTwoFactorSignIn: handleCompleteTwoFactorSignIn,
			refreshToken: handleRefreshToken,
			signOut: handleSignOut,
			googleSignIn: handleGoogleSignIn,
			isAuthenticated: handleIsAuthenticated,
			getSession,
			isLoading,
			error,
		};
	}, [
		error,
		handleCompleteTwoFactorSignIn,
		handleGoogleSignIn,
		handleIsAuthenticated,
		handleRefreshToken,
		handleSignIn,
		handleSignOut,
		isLoading,
	]);

	return <AuthContext value={contextValue}>{children}</AuthContext>;
};

export default AuthProvider;
